import * as React from 'react';
import { AutocompleteArrayInput, useTranslate } from 'react-admin';
import { useFormContext } from 'react-hook-form';
import moment from 'moment';
import { toJsDate } from '../../utils/dateUtils';
import { apiGet, useApiData } from '../../services/apiClient';
import { db } from '../../database/firebase';
import { collection, getDocs, query, where, documentId } from 'firebase/firestore';

const fetchUpcomingFromApi = async () => {
  const params = new URLSearchParams({
    page: '1',
    perPage: '200',
    status: 'upcoming',
    sort: 'startdate',
    order: 'asc',
    isActive: 'true',
  });
  const json = await apiGet(`events?${params.toString()}`);
  return (json?.data ?? []).map((row) => ({ ...row, id: row.id }));
};

const fetchEventsByIdsFromApi = async (ids) => {
  const results = await Promise.all(
    ids.map(async (id) => {
      try {
        const row = await apiGet(`events/${encodeURIComponent(id)}`);
        return row?.id ? row : null;
      } catch {
        return null;
      }
    }),
  );
  return results.filter(Boolean);
};

const UpcomingEventsSelectInput = ({ record }) => {
  const translate = useTranslate();
  const { setValue } = useFormContext();
  const [isLoading, setIsLoading] = React.useState(true);
  const [events, setEvents] = React.useState([]);
  const [loadError, setLoadError] = React.useState(null);

  React.useEffect(() => {
    let cancelled = false;

    const run = async () => {
      try {
        setLoadError(null);

        if (useApiData) {
          const upcoming = await fetchUpcomingFromApi();
          let preSelected = [];
          if (record?.events?.length) {
            preSelected = await fetchEventsByIdsFromApi(record.events);
          }
          const merged = [...upcoming, ...preSelected].filter(
            (row, index, self) => index === self.findIndex((t) => t.id === row.id),
          );
          if (!cancelled) setEvents(merged);
        } else {
          const q = query(
            collection(db, 'events-v2'),
            where('startdate', '>=', new Date()),
            where('isActive', '==', true),
          );
          const querySnapshot = await getDocs(q);
          let data = [];
          querySnapshot.forEach((docSnap) => {
            data.push({ id: docSnap.id, ...docSnap.data() });
          });

          if (record?.events?.length) {
            const q2 = query(
              collection(db, 'events-v2'),
              where(documentId(), 'in', record.events.slice(0, 10)),
            );
            const snap2 = await getDocs(q2);
            snap2.forEach((docSnap) => {
              data.push({ id: docSnap.id, ...docSnap.data() });
            });
          }

          const merged = data.filter(
            (row, index, self) => index === self.findIndex((t) => t.id === row.id),
          );
          if (!cancelled) setEvents(merged);
        }
      } catch (error) {
        console.error('UpcomingEventsSelectInput:', error);
        if (!cancelled) {
          setLoadError(error?.message || 'Failed to load events');
          setEvents([]);
        }
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };

    run();

    return () => {
      cancelled = true;
    };
  }, [record]);

  React.useEffect(() => {
    if (!isLoading && record?.events) {
      setValue('events', record.events);
    }
  }, [isLoading, record, setValue]);

  if (isLoading) {
    return <span style={{ marginBottom: 16 }}>{translate('groupEvents.loading_upcoming')}</span>;
  }

  if (loadError) {
    return (
      <span style={{ color: 'red', marginBottom: 16 }}>
        {translate('groupEvents.load_error', { error: loadError })}
      </span>
    );
  }

  if (!events.length) {
    return <span style={{ marginBottom: 16 }}>{translate('groupEvents.no_upcoming')}</span>;
  }

  return (
    <AutocompleteArrayInput
      optionText={(r) => {
        const d = toJsDate(r.startdate);
        return `${r.name} (${d ? moment(d).format('LL, LT') : 'N/A'})`;
      }}
      optionValue="id"
      fullWidth
      label={translate('groupEvents.select_events')}
      clearOnBlur={false}
      choices={events}
      source="events"
    />
  );
};

export default UpcomingEventsSelectInput;
